import {
  Box,
  Chip,
  Drawer,
  Divider,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";

const DrawerContent = styled(Box)(({ theme }) => ({
  width: "34vw",
  minWidth: 360,
  maxWidth: 480,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 2.5, 2, 2.5),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
  flexShrink: 0,
}));

const Body = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(2.5),
  flex: 1,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: theme.spacing(1.25),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));

const MetaGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.4),
}));

const MetaLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.72rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.palette.text.disabled,
}));

const MetaValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const ChipGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.75),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: "6px",
  fontSize: "0.78rem",
  height: 28,
  backgroundColor: theme.palette.action.hover,
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),
  },
}));

const StatusBadge = ({ isActive }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.5,
      py: 0.4,
      borderRadius: "6px",
      backgroundColor: isActive ? "#f0fdf4" : "#fef2f2",
      color: isActive ? "#15803d" : "#dc2626",
      fontSize: "0.8rem",
      fontWeight: 500,
      width: "fit-content",
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: isActive ? "#16a34a" : "#dc2626",
        flexShrink: 0,
      }}
    />
    {isActive ? "Active" : "Inactive"}
  </Box>
);

const EmptyState = () => (
  <Typography fontSize="0.8rem" color="text.disabled">
    None configured
  </Typography>
);

const ChipSection = ({ label, icon, items }) => (
  <Box>
    <SectionLabel>
      {icon}
      {label}
    </SectionLabel>
    {items?.length ? (
      <ChipGroup>
        {items.map((item) => (
          <StyledChip
            key={item.code}
            label={item.name}
            title={item.code}
            size="small"
          />
        ))}
      </ChipGroup>
    ) : (
      <EmptyState />
    )}
  </Box>
);

const RoutingProfileSheet = ({ profile, open, onClose }) => {
  if (!profile) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlotProps={{ backdrop: { onClick: onClose } }}
      PaperProps={{ sx: { boxShadow: "-4px 0 24px rgba(0,0,0,0.08)" } }}
    >
      <DrawerContent>
        <Header>
          <IconWrapper>
            <AccountTreeOutlinedIcon
              sx={{ color: "primary.main", fontSize: 22 }}
            />
          </IconWrapper>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={600}
              fontSize="1rem"
              noWrap
              title={profile.name}
            >
              {profile.name ?? "Routing Profile"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {profile.code ?? `Profile #${profile.id}`}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "text.secondary", mt: -0.25, mr: -0.5, flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>

        <Divider />

        <Body>
          <Box>
            <SectionLabel>Overview</SectionLabel>

            {profile.description && (
              <Typography
                fontSize="0.85rem"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.6 }}
              >
                {profile.description}
              </Typography>
            )}

            <MetaGrid>
              <MetaItem>
                <MetaLabel>Status</MetaLabel>
                <StatusBadge isActive={profile.isActive} />
              </MetaItem>

              <MetaItem>
                <MetaLabel>Precedence</MetaLabel>
                <MetaValue>{profile.precedence ?? "—"}</MetaValue>
              </MetaItem>

              <MetaItem>
                <MetaLabel>Selection Strategy</MetaLabel>
                <MetaValue>{profile.selectionStrategy ?? "—"}</MetaValue>
              </MetaItem>

              <MetaItem>
                <MetaLabel>Profile ID</MetaLabel>
                <MetaValue>#{profile.id}</MetaValue>
              </MetaItem>
            </MetaGrid>
          </Box>

          <Divider />

          <ChipSection
            label="Routing Types"
            icon={<HubOutlinedIcon sx={{ fontSize: 14 }} />}
            items={profile.routingTypes}
          />

          <Divider />

          <ChipSection
            label="Card Types"
            icon={<CreditCardOutlinedIcon sx={{ fontSize: 14 }} />}
            items={profile.cardTypes}
          />

          <Divider />

          <ChipSection
            label="Processors"
            icon={<TuneIcon sx={{ fontSize: 14 }} />}
            items={profile.processors}
          />
        </Body>
      </DrawerContent>
    </Drawer>
  );
};

export default RoutingProfileSheet;
